"use client";

import * as React from "react"
import { zodResolver } from "@hookform/resolvers/zod"
import { useForm, set } from "react-hook-form"
import * as z from "zod"
import { toast } from "@/components/ui/use-toast"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import { buttonVariants } from "@/components/ui/button"
import { Icons } from "@/components/icons"
import { cn } from "@/lib/utils"
import { praiseSchema } from "@/lib/validations/review";  
import { useRouter } from "next/navigation";
import { draftMode } from "next/headers";
import { usePostHog } from "posthog-js/react";
import { Rating, ThinStar } from '@smastrom/react-rating';
import { shadeColor } from "@/lib/utils";

interface SendPraiseFormProps extends React.HTMLAttributes<HTMLFormElement> {
    reviewerUserId?: string;
  }  

type FormData = z.infer<typeof praiseSchema>

export function SendPraiseForm({ className, reviewerUserId, ...props }: SendPraiseFormProps) {
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormData>({
    resolver: zodResolver(praiseSchema),
    defaultValues: {
        name: "",
        text: "",
        title: "",
        rating: 5
      },
  })

  const [isSaving, setIsSaving] = React.useState<boolean>(false);
  const [selectedRating, setSelectedRating] = React.useState<number>(5);
  const router = useRouter();
  const posthog = usePostHog();

  const myStyles = {
    itemShapes: ThinStar,
    activeFillColor: "#3B82F6",
    inactiveFillColor: shadeColor("#3B82F6", 50)
  }

  async function onSubmit(data: FormData) {
    setIsSaving(true);
    
    // TODO: make userId optional on UserReview schema (and test)
    const completeData = { ...data, reviewerUserId: reviewerUserId, userId: 'cljoicglo0000kx08sjct1nk0', origin: 'superstack' };

    const response = await fetch(`/api/reviews/praise`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(completeData),
    })

    setIsSaving(false);

    if (!response.ok) {
        return toast({
            title: "Something went wrong.",
            description: "Unable to generate praise at this time.",
            variant: "destructive",
        }) 
    }

    posthog.capture(
      'Praise submitted',
      {
        title: data.title,
        text: data.text,
        rating: data.rating,
        name: data.name
      }
    )

    const responseJSON = await response.json();
    const reviewId = encodeURIComponent(responseJSON.id);
    return router.push(`/send-praise?id=${reviewId}`);
  }

  return (
    <form className={className} onSubmit={handleSubmit(onSubmit)} {...props}>
        <div className="mt-1 flex items-center justify-center">
          <Rating
              className="mt-1"
              style={{ maxWidth: 250 }}
              onChange={setSelectedRating}
              value={selectedRating}
              halfFillMode='svg'
              itemStyles={myStyles}
          />
          <Input
              id="rating"
              className="mt-1"
              size={32}
              style={{ display: 'none' }}
              value={selectedRating}
              {...register("rating")}
          />
          {errors?.rating && (
              <p className="px-1 text-xs text-red-600">{errors.rating.message}</p>
          )}
        </div>
        <div className="mt-4">
            <Label className="" htmlFor="name">
                Your Name
            </Label>
            <Input
                id="name"
                className="mt-1"
                size={32}
                {...register("name")}
            />
            {errors?.name && (
                <p className="px-1 text-xs text-red-600">{errors.name.message}</p>
            )}
        </div>
        <div className="mt-4">
                <Label className="" htmlFor="title">
                    Praise Summary
                </Label>
            <Input
                id="title"
                className="mt-1"
                size={32}
                {...register("title")}
                placeholder="Such good energy!"
            />
            {errors?.title && (
                <p className="px-1 text-xs text-red-600">{errors.title.message}</p>
            )}
        </div>
        <div className="mt-4">
            <Label className="" htmlFor="text">
                Praise
            </Label>
            <Textarea
                id="text"
                className="mt-1"
                {...register("text")}
                placeholder="You have made such a big difference to my life..."
            />
            {errors?.text && (
                <p className="px-1 text-xs text-red-600">{errors.text.message}</p>
            )}
        </div>
      <div className="w-full z-10 flex items-center justify-center p-2">
        <button
          type="submit"
          className={cn(buttonVariants(), className, "my-2 bg-blue-500 w-full")}>
          {isSaving && (
                <Icons.spinner className="mr-2 h-4 w-4 animate-spin" />
            )}
          <span>Generate Praise Link</span>
        </button>
      </div>
    </form>
  )
}
